import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  ScaleControl,
  FeatureGroup,
  useMap
} from 'react-leaflet';
import L, { LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';
// @ts-ignore
import decodePolyline from 'decode-google-map-polyline';
import { pickupIconSvg, deliveryIconSvg, garageIconSvg, unscheduledIconSvg } from '@/components/Common/textMarkerMaker';
import { API_KEY } from '@/config/constants';
import utilMaps from '../utils/utilMaps';
import LayersPanelDetails from './RouteListMap/RouteMap/LayersPanelDetails';
import CustomZoomControl from './RouteListMap/RouteMap/CustomZoomControl';
import { IconButton } from '@mui/material';
import { LayersOutlined } from '@mui/icons-material';

interface Stop {
  id?: string | number;
  idNumber?: string | number;
  type: string;
  location: {
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  shipments_to_load?: any[];
  shipments_to_unload?: any[];
  label?: string;
  tourId?: string;
  stopIndex?: number;
  __unscheduled?: boolean;
  showOnMap?: boolean;
  includeInETA?: boolean;
  includeInCapacity?: boolean;
}

interface Props {
  polylines: { id: string; polyline: string }[];
  stops: Stop[];
  routeColors: Record<string, string>;
  companyLocation?: number[];
  onStopClick?: (stop: Stop) => void;
  onStopDoubleClick?: (stop: Stop) => void;
  selectedStopId?: string | number;
  visibleTours: Record<string, boolean>;
  setVisibleTours: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  adhocStops?: Stop[];
  editMode?: boolean;
}

type DecodedPolyline = {
  tourId: string;
  coords: LatLngTuple[];
};

type Beeline = {
  key: string;
  from: LatLngTuple;
  to: LatLngTuple;
  tourId: string;
};

type MaskSeg = {
  key: string;
  from: LatLngTuple;
  to: LatLngTuple;
  tourId: string;
};

const normalizeLatLng = (latRaw: any, lngRaw: any): [number, number] => {
  let lat = Number(latRaw);
  let lng = Number(lngRaw);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [NaN as any, NaN as any];
  const outOfRange = Math.abs(lat) > 90 || Math.abs(lng) > 180;
  const looksSwapped = (Math.abs(lat) < 20 && Math.abs(lng) > 30);

  if (outOfRange || looksSwapped) {
    return [lng, lat];
  }
  return [lat, lng];
};

const SelectedStopFly: React.FC<{
  stop: { lat: number; lng: number } | null;
  minZoom?: number;
  durationSec?: number;
}> = ({ stop, minZoom = 14, durationSec = 0.8 }) => {
  const map = useMap();

  useEffect(() => {
    if (!stop) return;
    const { lat, lng } = stop;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const targetZoom = Math.max(map.getZoom?.() ?? 0, minZoom);
    (map as any).stop?.();
    if ((map.getZoom?.() ?? 0) >= minZoom) {
      map.panTo([lat, lng], { animate: true, duration: durationSec, easeLinearity: 0.2 });
    } else {
      map.flyTo([lat, lng], targetZoom, { animate: true, duration: durationSec });
    }
  }, [map, stop?.lat, stop?.lng, minZoom, durationSec]);

  return null;
};

//@ts-ignore
const FitBoundsController = ({ coords }: { coords: LatLngTuple[] }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || coords.length === 0) return;
    const clean = coords.filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    if (clean.length === 0) return;
    const bounds = L.latLngBounds(clean);
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [map, coords]);
  return null;
};

const useBeelinesAndMasks = (stops: Stop[], visibleTours: Record<string, boolean>) => {
  const hasCoords = (s: Stop | undefined) => {
    const lat = s?.location?.coordinates?.latitude as any;
    const lng = s?.location?.coordinates?.longitude as any;
    return Number.isFinite(lat) && Number.isFinite(lng);
  };
  const getLL = (s: Stop) => {
    const lat = s?.location?.coordinates?.latitude as any;
    const lng = s?.location?.coordinates?.longitude as any;
    return normalizeLatLng(lat, lng) as LatLngTuple;
  };

  return React.useMemo(() => {
    const byTour: Record<string, Stop[]> = {};
    (stops || []).forEach(s => {
      if (!s?.tourId) return;
      if (visibleTours[s.tourId] === false) return;
      if (s?.includeInETA === false) return;
      if (!hasCoords(s)) return;
      (byTour[s.tourId] ||= []).push(s);
    });

    const beelines: Beeline[] = [];
    const masks: MaskSeg[] = [];

    Object.entries(byTour).forEach(([tourId, list]) => {
      const arr = [...list].sort((a, b) => (a.stopIndex ?? 0) - (b.stopIndex ?? 0));

      for (let i = 0; i < arr.length; i++) {
        const a = arr[i - 1];
        const b = arr[i];
        const c = arr[i + 1];
        if (!a || !b || !c) continue;

        const isProv = (b as any)?.meta?.provisional === true;
        if (!isProv) continue;

        // guards already applied above; still double-check
        if (!hasCoords(a) || !hasCoords(b) || !hasCoords(c)) continue;

        const aLL = getLL(a);
        const bLL = getLL(b);
        const cLL = getLL(c);

        beelines.push({ key: `${tourId}-a-b-${a.id}-${b.id}`, from: aLL, to: bLL, tourId });
        beelines.push({ key: `${tourId}-b-c-${b.id}-${c.id}`, from: bLL, to: cLL, tourId });
        masks.push({ key: `${tourId}-mask-${a.id}-${c.id}`, from: aLL, to: cLL, tourId });
      }
    });

    return { beelines, masks };
  }, [stops, visibleTours]);
};

const VehicleRouteDetailsMap: React.FC<Props> = ({
  polylines,
  stops,
  routeColors,
  companyLocation,
  onStopClick,
  onStopDoubleClick,
  selectedStopId,
  visibleTours,
  setVisibleTours,
  adhocStops = [],
  editMode = false,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const pendingFlyRef = useRef<{ lat: number; lng: number } | null>(null);
  const [showStops, setShowStops] = useState(true);
  const [showDepots, setShowDepots] = useState(true);
  const [showTouren, setShowTouren] = useState(true);
  const [showPickup, setShowPickup] = useState(true);
  const [showDelivery, setShowDelivery] = useState(true);
  const [baseLayer, setBaseLayer] = useState('Standard');
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  const decodedPolylines: DecodedPolyline[] = useMemo(() => {
    return polylines.map(({ id, polyline }) => {
      try {
        const coords = decodePolyline(polyline).map((p: any) => {
          const [lat, lng] = normalizeLatLng(p.lat, p.lng);
          return [lat, lng] as LatLngTuple;
        });
        return { tourId: id, coords };
      } catch {
        return { tourId: id, coords: [] };
      }
    });
  }, [polylines]);

  const center: LatLngTuple = useMemo(() => {
    const first = decodedPolylines.find((p) => p.coords.length > 0);
    if (first) return first.coords[0];
    if (companyLocation?.length === 2) {
      const [lat, lng] = normalizeLatLng(companyLocation[1], companyLocation[0]);
      return [lat, lng];
    }
    return [51.6841, 8.7301];
  }, [decodedPolylines, companyLocation]);

  // merge scheduled + adhoc (adhoc marked as unscheduled)
  const allStops = useMemo(() => {
    const validAdhoc = (adhocStops || [])
      .filter(s => {
        const lat = s?.location?.coordinates?.latitude;
        const lng = s?.location?.coordinates?.longitude;
        return typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng);
      })
      .map(s => ({ ...s, __unscheduled: true })); // flag
    return [...(stops || []), ...validAdhoc];
  }, [stops, adhocStops]);

  const selectedStopPos = useMemo(() => {
    if (!selectedStopId) return null;
    const s = allStops.find(st => String(st.id) === String(selectedStopId));
    if (!s) return null;

    const lat = s?.location?.coordinates?.latitude;
    const lng = s?.location?.coordinates?.longitude;

    if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
      return null; // <-- NO COORDS => don't try to fly/pan
    }
    const [nlat, nlng] = normalizeLatLng(lat, lng);
    return { lat: nlat, lng: nlng };
  }, [selectedStopId, allStops]);

  // Always compute hook AFTER allStops exists, then gate by editMode
  const bm = useBeelinesAndMasks(allStops, visibleTours);
  const beelines = editMode ? bm.beelines : [];
  const masks: MaskSeg[] = [];

  const fitCoords = useMemo(() => {
    const arr: LatLngTuple[] = [];

    decodedPolylines
      .filter(p => visibleTours[p.tourId])
      .forEach(p => arr.push(...p.coords));

    (adhocStops || []).forEach(s => {
      if (s.showOnMap === false) return;
      const [lat, lng] = normalizeLatLng(
        s.location.coordinates.latitude,
        s.location.coordinates.longitude
      );
      if (Number.isFinite(lat) && Number.isFinite(lng)) arr.push([lat, lng]);
    });

    return arr.filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
  }, [decodedPolylines, adhocStops, visibleTours]);

  const depotMarker = useMemo(() => {
    const firstDepot = stops.find((s) => s.type === 'depot');
    if (!firstDepot) return null;
    return utilMaps.renderDepotMarkerFromLocation(firstDepot.location);
  }, [stops]);

  const lastGarageStopId = useMemo(() => {
    const garage = [...allStops].reverse().find(s => s.type === 'garage');
    return garage?.id ?? null;
  }, [allStops]);

  const markers = useMemo(() => {
    if (!showStops) return [];
    return allStops
      .filter((stop) => {
        const lat = stop?.location?.coordinates?.latitude;
        const lng = stop?.location?.coordinates?.longitude;
        if (typeof lat !== 'number' || typeof lng !== 'number') return false;
        if (stop.type === 'depot') return false;

        if (stop.tourId && stop.__unscheduled !== true && visibleTours[stop.tourId] === false) return false;
        const isPickup = Array.isArray(stop.shipments_to_load) && stop.shipments_to_load.length > 0;
        const isDelivery = Array.isArray(stop.shipments_to_unload) && stop.shipments_to_unload.length > 0;

        if (isPickup && !showPickup) return false;
        if (isDelivery && !isPickup && !showDelivery) return false;

        return true;
      })
      .map((stop, idx) => {
        const [lat, lng] = normalizeLatLng(
          stop.location.coordinates.latitude,
          stop.location.coordinates.longitude
        );
        const coords: LatLngTuple = [lat, lng];
        const isPickup = Array.isArray(stop.shipments_to_load) && stop.shipments_to_load.length > 0;
        const isDelivery = Array.isArray(stop.shipments_to_unload) && stop.shipments_to_unload.length > 0;
        const isSelected = selectedStopId === stop.id;
        const isUnscheduled = stop.__unscheduled === true;
        let iconSvg: string;
        if (stop.id === lastGarageStopId && stop.type === 'garage') {
          iconSvg = garageIconSvg(undefined, isSelected);
        } else if (isUnscheduled) {
          iconSvg = unscheduledIconSvg(isSelected);
        } else if (isPickup) {
          iconSvg = pickupIconSvg(idx + 1, isSelected);
        } else {
          iconSvg = deliveryIconSvg(idx + 1, isSelected);
        }

        const iconUrl = 'data:image/svg+xml;utf8,' + encodeURIComponent(iconSvg);

        const markerIcon = L.icon({
          iconUrl,
          iconSize: [50, 60],
          iconAnchor: [25, 60],
          popupAnchor: [0, -60],
          className: stop.id === selectedStopId ? 'highlighted-marker' : '',
        });

        return (
          <Marker
            key={`stop-${stop.id ?? idx}`}
            icon={markerIcon}
            position={coords}
            eventHandlers={{
              click: () => {
                const stopId = stop.id || stop.idNumber || `${stop.tourId}-${stop.stopIndex}`;
                onStopClick?.({ ...stop, id: stopId });
              },
              dblclick: () => {
                if (stop.type === 'garage' || stop.type === 'depot') return;
                const stopId = stop.id || stop.idNumber || `${stop.tourId}-${stop.stopIndex}`;
                onStopDoubleClick?.({ ...stop, id: stopId });
              },
            }}
          />
        );
      });
  }, [
    allStops,
    onStopClick,
    onStopDoubleClick,
    selectedStopId,
    showPickup,
    showDelivery,
    showStops,
    visibleTours,
    lastGarageStopId,
  ]);

  const getTileUrl = useCallback(() => {
    if (baseLayer === 'LKW') {
      return `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=lite.day&features=vehicle_restrictions:active_and_inactive,congestion_zones:all,environmental_zones:all&apiKey=${API_KEY}&lang=de`;
    }
    if (baseLayer === 'Satellit') {
      return `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=explore.satellite.day&apiKey=${API_KEY}&lang=de`;
    }
    return `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=lite.day&apiKey=${API_KEY}&lang=de`;
  }, [baseLayer]);

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <IconButton
        onClick={() => setShowLayersPanel(true)}
        sx={{
          position: 'absolute',
          top: 100,
          left: 12,
          zIndex: 1001,
          backgroundColor: '#fff',
          borderRadius: 2,
          padding: '6px',
          boxShadow: 3,
          '&:hover': { backgroundColor: '#fff' },
        }}
      >
        <LayersOutlined />
      </IconButton>

      {showLayersPanel && (
        <LayersPanelDetails
          showStops={showStops}
          showDepots={showDepots}
          showTouren={showTouren}
          showPickup={showPickup}
          showDelivery={showDelivery}
          selectedBase={baseLayer}
          visibleTours={visibleTours}
          onToggleTourVisibility={(tourId) =>
            setVisibleTours((prev) => ({
              ...prev,
              [tourId]: !prev[tourId],
            }))
          }
          onToggleStops={() => setShowStops(!showStops)}
          onToggleDepot={() => setShowDepots(!showDepots)}
          onToggleTouren={() => setShowTouren(!showTouren)}
          onTogglePickup={() => setShowPickup(!showPickup)}
          onToggleDelivery={() => setShowDelivery(!showDelivery)}
          onChangeBase={setBaseLayer}
          onClose={() => setShowLayersPanel(false)}
        />
      )}

      <MapContainer
        center={center}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        doubleClickZoom={false}
        //@ts-ignore
        whenCreated={(map: L.Map) => {
          mapRef.current = map;
        }}
      >
        <TileLayer
          url={getTileUrl()}
          attribution={`Map © ${new Date().getFullYear()} HERE`}
          subdomains="1234"
          maxZoom={20}
          updateWhenIdle={true}
        />
        {!selectedStopPos && <FitBoundsController coords={fitCoords} />}
        <CustomZoomControl />
        <ScaleControl />
        {selectedStopPos && <SelectedStopFly stop={selectedStopPos} minZoom={14} durationSec={1.0} />}
        <FeatureGroup>
          {showTouren &&
            decodedPolylines
              .filter(({ tourId }) => visibleTours[tourId])
              .map(({ coords, tourId }, idx) => (
                <Polyline
                  key={`polyline-${tourId}-${idx}`}
                  positions={coords}
                  pathOptions={{
                    color: routeColors[tourId] || '#1976d2',
                    weight: 4,
                    opacity: 0.6,
                  }}
                />
              ))}

          {/* dotted beelines for provisional insert */}
          {beelines.map(b => (
            <Polyline
              key={`beeline-${b.key}`}
              positions={[b.from, b.to]}
              pathOptions={{
                color: '#000000',
                weight: 3,
                opacity: 0.9,
                dashArray: '6 8',
              }}
            />
          ))}

          {showDepots && depotMarker}
          {markers}
        </FeatureGroup>
      </MapContainer>
    </div>
  );
};

export default VehicleRouteDetailsMap;
